import { useMemo, type FC } from 'react';
import {
  ButtonField,
  InputField,
  Smokebreak,
  TextareaField,
} from '@wk/components-react16';
import { useFormik, type FormikErrors } from 'formik';
import { cloneDeep } from 'lodash';

import type { Checkpoint, CheckpointParameter } from '@/domains/checkpoint/checkpoint.types';
import { CheckpointParameterType } from '@/domains/checkpoint/checkpoint.types';
import { object, string, mixed } from 'yup';
import { array } from 'yup';
import { useMutation } from '@tanstack/react-query';
import { updateCheckpointParameters } from '@/domains/application/applicationApi';
import useToast from '@/shared/stores/toastStore';
import { useShallow } from 'zustand/react/shallow';
import { AxiosError } from 'axios';
import { IconLoader2 } from '@tabler/icons-react';
import { getCheckpointVersion } from '@/lib/checkpointKeys';

interface CheckpointConfigProps {
  isOpen: boolean;
  onClose: () => void;
  bitId: string;
  configurationId: string;
  checkpoint: Checkpoint | null;
  refetch: () => void;
  disabled: boolean;
}

const getParameterValidationSchema = (paramType: CheckpointParameterType) => {
  switch (paramType) {
    case CheckpointParameterType.String:
      return string().required('This field is required');
    case CheckpointParameterType.Boolean:
      return mixed().test('is-boolean', 'Must be true or false', (value) => {
        if (value === undefined || value === null || value === '') return true;
        return (
          value === 'true' ||
          value === 'false' ||
          value === true ||
          value === false
        );
      });
    case CheckpointParameterType.Integer:
      return mixed().test('is-integer', 'Must be a valid integer', (value) => {
        if (value === undefined || value === null || value === '') return true;
        const num = Number(value);
        return Number.isInteger(num) && !Number.isNaN(num);
      });
    case CheckpointParameterType.Float:
      return mixed().test('is-float', 'Must be a valid number', (value) => {
        if (value === undefined || value === null || value === '') return true;
        const num = Number(value);
        return !Number.isNaN(num);
      });
    case CheckpointParameterType.List:
      return string().required('This field is required');
    case CheckpointParameterType.Dictionary:
      return string().required('This field is required');
    default:
      return string().optional();
  }
};

const CheckpointConfig: FC<CheckpointConfigProps> = ({
  isOpen,
  onClose,
  checkpoint,
  bitId,
  configurationId,
  refetch,
  disabled,
}) => {
  const hasException = useMemo(() => {
    return Boolean(checkpoint?.exceptions && checkpoint.exceptions[0]);
  }, [checkpoint]);

  const checkpointVersion = getCheckpointVersion(checkpoint);

  const { showToast } = useToast(
    useShallow((state) => ({ showToast: state.showToast })),
  );

  const {
    mutate: updateCheckpointParametersMutation,
    isPending: isUpdatingParameters,
  } = useMutation({
    mutationFn: updateCheckpointParameters,
    onSuccess: () => {
      showToast('success', {
        message: 'Parameters updated successfully',
      });
      refetch();
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        showToast('error', {
          message:
            error.response?.data.title ||
            error.response?.data.message ||
            'Something went wrong',
        });
      } else {
        showToast('error', {
          message: 'Something went wrong',
        });
      }
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
    errors,
  } = useFormik<{
    parameters: CheckpointParameter[];
  }>({
    initialValues: {
      parameters:
        cloneDeep(checkpoint?.parameters)?.map((param) => ({
          ...param,
          paramValue: param.paramValue || '',
        })) || [],
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      parameters: array().of(
        object({
          paramName: string().optional(),
          paramType: string().optional(),
          paramValue: mixed().test(
            'dynamic-validation',
            'Invalid value',
            function (value) {
              const { paramType } = this.parent;
              if (!paramType) return true;
              if (this.parent.isRuntime) return true;
              if (this.parent.isOptional) return true;
              if (!checkpoint?.isScored) return true;

              const schema = getParameterValidationSchema(
                paramType as CheckpointParameterType,
              );
              try {
                schema.validateSync(value);
                return true;
              } catch (error) {
                return this.createError({ message: (error as Error).message });
              }
            },
          ),
        }),
      ),
    }),
    onSubmit: (values) => {
      updateCheckpointParametersMutation({
        bitId,
        configurationId,
        checkpointId: checkpoint?.id || '',
        checkpointVersion,
        parameters: values.parameters,
      });
    },
  });

  return (
    <Smokebreak
      isOpen={isOpen}
      position="right"
      onUserRequest={() => onClose()}
    >
      <h2 className="p-3 font-semibold text-lg" slot="smokebreakHeader">
        {checkpoint?.id}
        {checkpointVersion !== null ? ` (v${checkpointVersion})` : ''}
      </h2>
      <div className="p-3 flex flex-col gap-4 w-full" slot="smokebreakBody">
        <InputField label="Category" size="small">
          <input disabled type="text" value={checkpoint?.category} />
        </InputField>
        <InputField label="Accountable (Division/Team)" size="small">
          <input disabled type="text" value={checkpoint?.accountable} />
        </InputField>
        <InputField label="Owner" size="small">
          <input disabled type="text" value={checkpoint?.owner || 'N/A'} />
        </InputField>
        <InputField label="Systems" size="small">
          <input disabled type="text" value={checkpoint?.referenceSystem} />
        </InputField>
        <TextareaField label="Checkpoint Description" size="small">
          <textarea disabled value={checkpoint?.description} />
        </TextareaField>
        {values.parameters.length > 0 && (
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold text-sm">Parameters</h3>
            <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
              {values.parameters.map((param, index) => (
                <InputField
                  key={param.paramName}
                  label={param.displayName}
                  size="small"
                  errors={
                    errors.parameters && errors.parameters[index]
                      ? [
                          {
                            label:
                              (
                                errors.parameters[
                                  index
                                ] as FormikErrors<CheckpointParameter>
                              ).paramValue || '',
                            labelId: param.paramName,
                          },
                        ]
                      : undefined
                  }
                  description={
                    param.isRuntime
                      ? 'This parameter is set at runtime and cannot be modified now'
                      : undefined
                  }
                >
                  <input
                    type="text"
                    name={`parameters[${index}].paramValue`}
                    value={param.paramValue}
                    onBlur={handleBlur}
                    disabled={param.isRuntime}
                    onChange={handleChange}
                  />
                </InputField>
              ))}
              <ButtonField>
                <button
                  disabled={
                    isSubmitting || !isValid || isUpdatingParameters || disabled
                  }
                  type="submit"
                >
                  Save Parameters
                  {isUpdatingParameters && (
                    <IconLoader2 className="animate-spin" />
                  )}
                </button>
              </ButtonField>
            </form>
          </div>
        )}
        {hasException && (
          <>
            <div className="flex flex-col gap-2">
              <h3 className="font-semibold text-sm">Exception</h3>
              <div className="flex flex-col gap-2">
                <TextareaField label="Exception Reason" size="small">
                  <textarea
                    disabled
                    value={checkpoint?.exceptions?.[0]?.description}
                  />
                </TextareaField>
                <span className="text-sm text-gray-500">
                  Created By: {checkpoint?.exceptions?.[0]?.createdBy}
                </span>
              </div>
            </div>
            {checkpoint?.exceptions?.[0]?.isApproved === null && (
              <p className="text-sm text-gray-500 text-center">
                This exception is awating approval
              </p>
            )}
            {checkpoint?.exceptions?.[0]?.isApproved && (
              <p className="text-sm text-gray-500 text-center">
                Approved By: {checkpoint?.exceptions?.[0]?.approvedBy}
              </p>
            )}
          </>
        )}
      </div>
    </Smokebreak>
  );
};

export default CheckpointConfig;
